import * as React from "react";
import {CSSProperties} from "react";
import {connect} from "react-redux";
import {none, option, Option} from "ts-option";
import User from "../../model/user/User";
import {api} from "../../api/api";
import Toast from "../helper/Toast";
import {RootState} from "../../store/reducer/root/RootState";
import IndividualContact from "../../model/contact/IndividualContact";
import Address from "../../model/address/Address";
import DayViewTimeLineItem from "./DayViewTimeLineItem";
import DayViewDayPicker from "./DayViewDayPicker";
import DayViewEngineerListItem from "./DayViewEngineerListItem";
import DayViewCurrentTimeBar from "./DayViewCurrentTimeBar";
import Appointment from "../../model/appointment/Appointment";
import AppointmentDetails, {AppointmentDataType} from "../../model/appointment/AppointmentDetails";
import DictionaryItem from "../../model/dictionary/DictionaryItem";
import JobAppointment from "../../model/appointment/job/JobAppointment";
import {isDefined} from "../../utils/Utils";
import AppointmentUtils from "../../utils/AppointmentUtils";
import moment = require("moment");
import uuidv4 = require("uuid/v4");
import deepEqual = require("deep-equal");

interface ComponentProps {
	/**
	 * Selected day to display on a diary.
	 */
	selectedDay: moment.Moment
	/**
	 * On day change callback.
	 * @param {moment.Moment} selectedDay
	 */
	onDayChange: (selectedDay: moment.Moment) => void
	/**
	 * Indicates if list of SE shows only engineers with appointments.
	 */
	filterWithAppointments: boolean;

	appointments: AppointmentDetails[]
}

interface ReduxProps {
	/**
	 * Sales Engineers
	 */
	salesEngineers: User[]
}

type DiaryTimelineProps = ComponentProps & ReduxProps

interface State {
	/**
	 * Start time of the timeline time headers.
	 */
	startTime: moment.Moment
	/**
	 * End time of the timeline time headers.
	 */
	endTime: moment.Moment

	/**
	 * Diary data for sales engineers.
	 */
	salesEngineersDiary: DiaryRowGrouped[]
	/**
	 * Diary data for unassigned appointments.
	 */
	unassignedDiaryGroups: DiaryRowGrouped[]
}

class DayViewTimeLine extends React.Component<DiaryTimelineProps, State> {
	/**
	 * Start hour of the timeline time headers.
	 */
	startHour: number = 6;
	/**
	 * End hour of the timeline time headers.
	 */
	endHour: number = 24;
	/**
	 * Timeline interval in minutes.
	 */
	interval: number = 15;

	timeBar: DayViewCurrentTimeBar;

	constructor(props: DiaryTimelineProps) {
		super(props);
		this.state = {...this.getTimelineHours(props.selectedDay), ...this.generateData(props)};
	}

	componentWillReceiveProps(nextProps: DiaryTimelineProps) {
		this.setState({...this.getTimelineHours(nextProps.selectedDay), ...this.generateData(nextProps)})
	}

	shouldComponentUpdate(nextProps: DiaryTimelineProps, nextState: State) {
		return !deepEqual(nextProps, this.props) || !deepEqual(nextState, this.state)
	}

	render() {
		const times = this.generateTime();
		const columnsCount = times.length * 60 / this.interval + 1;
		return (
			<React.Fragment>
				<div className="c-day-view_header"
					 style={{gridTemplateColumns: `310px repeat(${((times.length - 1) * 60 / this.interval) + 2}, 1fr)`}}>
					<DayViewDayPicker date={this.props.selectedDay}
									  onNextDayClick={() => this.props.onDayChange(
										  moment(this.props.selectedDay).add(1, "d"))}
									  onPreviousDayClick={() => this.props.onDayChange(
										  moment(this.props.selectedDay).subtract(1, "d"))}/>
					{this.renderHeaderTimes(times)}
				</div>
				<div className="c-day-view_se-list_wrapper">
					<div className={"c-day-view_se-list"}
						 style={{gridTemplateColumns: `310px repeat(${((times.length - 1) * 60 / this.interval) + 2}, 1fr)`}}
						 onLoad={() => {
							 if (this.timeBar) {
								 this.timeBar.refresh();
							 }
						 }}>
						{moment(this.props.selectedDay).isSame(moment(), "day") &&
						<DayViewCurrentTimeBar ref={ref => this.timeBar = ref}/>
						}
						{this.renderSalesEngineers(columnsCount)}
					</div>
				</div>
			</React.Fragment>
		);
	}

	renderHeaderTimes(times: moment.Moment[]) {
		return times.map((time, index) => {
			const isLast = index == times.length - 1;
			const startColumn = 3 + index * 60 / this.interval;
			//if last, just render time in next column
			const endColumn = startColumn + (isLast ? 1 : 60 / this.interval);
			const columns = `${startColumn} / ${endColumn}`;
			const style: CSSProperties = {
				gridColumn: columns
			};
			return (
				<div key={index} className="c-day-view_header_item" style={style} id={`time_${index}`}>
					<span className={"c-day-view_header_item_content"}>
						{time.format("k")}
					</span>
				</div>
			)
		})
	}

	/**
	 * Generate list of moments for headers.
	 * @return {moment.Moment[]}
	 */
	generateTime() {
		const startTime = moment(this.state.startTime).diff(moment().startOf("day"), "minutes");
		const endTime = moment(this.state.endTime).diff(moment().startOf("day"), "minutes");
		const time = [];
		for (let t = startTime; t <= endTime; t += 60)
			time.push(moment().startOf("day").add(t, "m"));
		return time;
	}

	/**
	 * Get timeline start and end hour for given day.
	 * @param {moment.Moment} selectedDay selected day for timeline.
	 * @return {{startTime: moment.Moment; endTime: moment.Moment}}
	 */
	getTimelineHours(selectedDay: moment.Moment): { startTime: moment.Moment, endTime: moment.Moment } {
		return {
			startTime: moment(selectedDay).hours(this.startHour).minutes(0).seconds(0),
			endTime: moment(selectedDay).hours(this.endHour).minutes(0).seconds(0)
		}
	}

	/**
	 * Renders sales engineers and "unassigned" row with appointments.
	 * @param {number} gridColumnsCount number of columns in the whole grid.
	 */
	renderSalesEngineers(gridColumnsCount: number) {
		const engineers: DiaryRowGrouped[] = this.props.filterWithAppointments ?
			this.state.salesEngineersDiary.filter(se => se.appointments.length !== 0) :
			this.state.salesEngineersDiary;
		return (
			<React.Fragment>
				{engineers.map((row, seIndex) => this.renderRow(row, seIndex * 2, gridColumnsCount))}
				{this.state.unassignedDiaryGroups.map(
					row => this.renderRow(row, this.state.salesEngineersDiary.length * 2, gridColumnsCount))}
			</React.Fragment>
		);
	}

	/**
	 * Render single sales engineer row.
	 * @param {DiaryRow} row row data.
	 * @param {number} previousRow previous row number.
	 * @param {number} gridColumnsCount grid total columns count.
	 */
	renderRow(row: DiaryRowGrouped, previousRow: number, gridColumnsCount: number) {
		const seKey = row.salesEngineer.map(se => se.id).getOrElse("unassigned");
		return (
			<React.Fragment key={uuidv4()}>
				<div style={{gridRow: previousRow * 2 + 3, gridColumn: "1"}}>
					{
						row.salesEngineer.isDefined ? <DayViewEngineerListItem user={row.salesEngineer.get} key={seKey}/> :
							<div className={"c-engineer-list-item-needs-attention"} key={seKey}>Needs Attention</div>
					}
				</div>
				{row.appointments.map(group => {
					const groupLength = group.length;
					return group.map((appointment, index) => {
						const style = DayViewTimeLine.calculateItemStyle(groupLength, index);
						return (
							<DayViewTimeLineItem key={appointment.appointment.id}
												 data={appointment}
												 gridRow={previousRow * 2 + 3}
												 startTime={this.state.startTime}
												 interval={this.interval}
												 eventPeriod={appointment.appointment.duration}
												 style={style}
												 sendSalesEngineerReminderSms={() => this.sendAppointmentReminder(
													 row.salesEngineer,
													 appointment.appointment.id)}
												 salesEngineer={row.salesEngineer}/>
						)
					})
				})
				}
				<div className="c-day-view_se-list_row-separator"
					 style={{gridRow: previousRow * 2 + 2, gridColumnEnd: gridColumnsCount}}/>
			</React.Fragment>
		);
	}

	sendAppointmentReminder(salesEngineer: Option<User>, appointmentId: string) {
		salesEngineer.map(se => {
			api.instance.appointmentApi.sendAppointmentReminderSMS(appointmentId, {salesEngineerId: se.id})
				.then(() => Toast.success("SMS to sales engineer was send", "Appointment reminder"))
				.catch(() => Toast.error("Sorry, something went wrong. Try again later.", "Appointment reminder"));
		})
	}

	/**
	 * Calculates styles form day view item depending on conflicted elements.
	 * @param {number} conflictGroupLength Length of conflicted group (hwo many elements in stack)
	 * @param {number} index Index of element in stack
	 * @return {React.CSSProperties} Css properties for styling calendar item.
	 */
	static calculateItemStyle(conflictGroupLength: number, index: number): CSSProperties {
		switch (conflictGroupLength) {
			case 1:
				return ({});
			case 2:
				return ({
					height: `40px`,
					marginTop: `${1 + 40 * index + index}px`,
					padding: "4px 10px"
				});
			case 3:
				return ({
					height: `${index === 1 ? 27 : 26}px`,
					marginTop: `${(index === 2 ? 2 : 1) + 26 * index + index}px`,
					padding: "4px 10px"
				});
			default:
				return ({
					height: `26px`,
					marginTop: `${1 + 26 * index + index}px`,
					padding: "4px 10px"
				});
		}
	}

	generateData = (props: DiaryTimelineProps) => {
		const salesEngineers = props.salesEngineers
		const seData: { [key: string]: DiaryRow } = {};
		salesEngineers.forEach(se => {
			seData[se.id] = {salesEngineer: option(se), appointments: []};
		});
		const unassignedAppointments: AppointmentDetails[] = []
		props.appointments.forEach(ad => {

			if (ad.isLeadAppointment) {
				const assigneeId = (ad.appointment as Appointment).assigneeId;
				if (assigneeId.isDefined && seData[assigneeId.get]) {
					seData[assigneeId.get].appointments = seData[assigneeId.get].appointments.concat(ad.toDiaryData())
				}
				else {
					unassignedAppointments.push(ad)
				}
			}
			else {
				const salesEngineers = (ad.appointment as JobAppointment).assignees.map(
					userId => props.salesEngineers.filter(se => se.id == userId)[0]).filter(isDefined);
				salesEngineers.forEach(se => seData[se.id].appointments = seData[se.id].appointments.concat(ad.toDiaryData()))
				if (salesEngineers.length == 0) {
					unassignedAppointments.push(ad);
				}
			}
		});

		const salesEngineersDiary: DiaryRowGrouped[] = Object.keys(seData).map(key => {
			const salesEngineerDiaryGroups = AppointmentUtils.splitDiaryRowsIntoCollisionGroups(seData[key].appointments);
			return {
				salesEngineer: seData[key].salesEngineer,
				appointments: salesEngineerDiaryGroups,
			}
		});

		let unassignedDiaryGroups: DiaryRowGrouped[] = [];
		if (unassignedAppointments.length) {
			unassignedDiaryGroups.push({
				salesEngineer: none,
				appointments: AppointmentUtils.splitDiaryRowsIntoCollisionGroups(
					unassignedAppointments.map(ad => ad.toDiaryData()))
			})
		}

		return {salesEngineersDiary, unassignedDiaryGroups}
	}
}

function mapStateToProps(state: RootState) {
	const salesEngineers = state.user.users.getOrElse([]).filter(u => !u.disabled && u.isSalesEngineer());
	return {salesEngineers}
}

/**
 * Diary row data.
 */
interface DiaryRow {
	salesEngineer: Option<User>
	appointments: DiaryRowAppointment[]
}

/**
 * Diary row data with grouped appointments.
 */
interface DiaryRowGrouped {
	salesEngineer: Option<User>
	appointments: DiaryRowAppointment[][]
}

/**
 * Diary appointment
 */
export interface DiaryRowAppointment {
	appointment: AppointmentDataType
	contact: IndividualContact
	address: Option<Address>
	type: Option<DictionaryItem>
	referenceNumber: Option<string>,
	isLeadAppointment: boolean
}

export default connect(mapStateToProps)(DayViewTimeLine);